import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowLeft, Shield } from 'lucide-react';

export default function PrivacyPage() {
  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b border-border bg-card">
        <div className="container-app py-4">
          <Link to="/" className="flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors w-fit">
            <ArrowLeft className="w-4 h-4" />
            Back to Home
          </Link>
        </div>
      </header>

      <main className="container-app py-12 max-w-4xl">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <div className="flex items-center gap-3 mb-8">
            <div className="w-12 h-12 bg-primary/10 rounded-sm flex items-center justify-center">
              <Shield className="w-6 h-6 text-primary" />
            </div>
            <div>
              <h1 className="text-4xl font-bold" style={{ fontFamily: 'Barlow Condensed' }}>
                PRIVACY POLICY
              </h1>
              <p className="text-muted-foreground">Last updated: February 2026</p>
            </div>
          </div>

          <div className="prose prose-invert max-w-none space-y-8">
            
            {/* Introduction */}
            <section>
              <h2 className="text-2xl font-bold mb-4" style={{ fontFamily: 'Barlow Condensed' }}>1. INTRODUCTION</h2>
              <p className="text-muted-foreground mb-4">
                The Hotshot Marketplace ("THM", "we", "us", or "our") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our website and services.
              </p>
              <p className="text-muted-foreground">
                We comply with the Australian Privacy Principles contained in the Privacy Act 1988 (Cth). By using our Platform, you consent to the collection and use of your information as described in this policy.
              </p>
            </section>

            {/* Information We Collect */}
            <section>
              <h2 className="text-2xl font-bold mb-4" style={{ fontFamily: 'Barlow Condensed' }}>2. INFORMATION WE COLLECT</h2>
              
              <h3 className="text-lg font-semibold mb-2">2.1 Information You Provide</h3>
              <p className="text-muted-foreground mb-4">We collect information you provide directly, including:</p>
              <ul className="list-disc list-inside text-muted-foreground space-y-2 mb-4">
                <li><strong>Account Information:</strong> Name, email address, phone number, password</li>
                <li><strong>Provider Information:</strong> Company name, ABN, service areas, equipment types</li>
                <li><strong>Job Information:</strong> Pickup and delivery addresses, freight details, photos</li>
                <li><strong>Payment Information:</strong> Credit card details (processed securely by Stripe)</li>
                <li><strong>Communications:</strong> Messages sent through our Platform, support inquiries</li>
              </ul>
              
              <h3 className="text-lg font-semibold mb-2">2.2 Information Collected Automatically</h3>
              <p className="text-muted-foreground mb-4">When you use our Platform, we automatically collect:</p>
              <ul className="list-disc list-inside text-muted-foreground space-y-2">
                <li><strong>Device Information:</strong> IP address, browser type, device type</li>
                <li><strong>Usage Information:</strong> Pages visited, features used, time spent on Platform</li>
                <li><strong>Location Information:</strong> General location based on IP address</li>
                <li><strong>Cookies:</strong> Small data files stored on your device to enhance your experience</li>
              </ul>
            </section>

            {/* How We Use Information */}
            <section>
              <h2 className="text-2xl font-bold mb-4" style={{ fontFamily: 'Barlow Condensed' }}>3. HOW WE USE YOUR INFORMATION</h2>
              <p className="text-muted-foreground mb-4">We use your information to:</p>
              <ul className="list-disc list-inside text-muted-foreground space-y-2">
                <li>Provide, maintain, and improve our Platform</li>
                <li>Process transactions and send related information</li>
                <li>Connect Customers with Providers</li>
                <li>Send notifications about jobs, quotes, and account activity</li>
                <li>Respond to your comments, questions, and support requests</li>
                <li>Monitor and analyze usage patterns and trends</li>
                <li>Detect, investigate, and prevent fraudulent or illegal activities</li>
                <li>Comply with legal obligations</li>
                <li>Send marketing communications (with your consent)</li>
              </ul>
            </section>

            {/* Information Sharing */}
            <section>
              <h2 className="text-2xl font-bold mb-4" style={{ fontFamily: 'Barlow Condensed' }}>4. INFORMATION SHARING</h2>
              
              <h3 className="text-lg font-semibold mb-2">4.1 With Other Users</h3>
              <p className="text-muted-foreground mb-4">
                When Customers and Providers interact through our Platform, certain information is shared to facilitate the transaction:
              </p>
              <ul className="list-disc list-inside text-muted-foreground space-y-2 mb-4">
                <li>Providers see Customer names, contact details, and job information</li>
                <li>Customers see Provider names, company details, ratings, and quotes</li>
              </ul>
              
              <h3 className="text-lg font-semibold mb-2">4.2 With Service Providers</h3>
              <p className="text-muted-foreground mb-4">We share information with third-party service providers who assist us in:</p>
              <ul className="list-disc list-inside text-muted-foreground space-y-2 mb-4">
                <li><strong>Payment Processing:</strong> Stripe processes all payments securely</li>
                <li><strong>Email Services:</strong> For sending transactional and marketing emails</li>
                <li><strong>SMS Services:</strong> For sending job notifications</li>
                <li><strong>Analytics:</strong> To understand Platform usage</li>
                <li><strong>Hosting:</strong> Cloud infrastructure providers</li>
              </ul>
              
              <h3 className="text-lg font-semibold mb-2">4.3 Legal Requirements</h3>
              <p className="text-muted-foreground">
                We may disclose your information if required by law, court order, or government regulation, or if we believe disclosure is necessary to protect our rights, your safety, or the safety of others.
              </p>
            </section>

            {/* Data Security */}
            <section>
              <h2 className="text-2xl font-bold mb-4" style={{ fontFamily: 'Barlow Condensed' }}>5. DATA SECURITY</h2>
              <p className="text-muted-foreground mb-4">
                We implement appropriate technical and organizational measures to protect your personal information, including:
              </p>
              <ul className="list-disc list-inside text-muted-foreground space-y-2">
                <li>Encryption of data in transit (SSL/TLS)</li>
                <li>Secure password hashing</li>
                <li>Regular security assessments</li>
                <li>Limited access to personal information by employees</li>
                <li>Secure payment processing through PCI-compliant providers</li>
              </ul>
              <p className="text-muted-foreground mt-4">
                However, no method of transmission over the Internet is 100% secure. While we strive to protect your information, we cannot guarantee absolute security.
              </p>
            </section>

            {/* Data Retention */}
            <section>
              <h2 className="text-2xl font-bold mb-4" style={{ fontFamily: 'Barlow Condensed' }}>6. DATA RETENTION</h2>
              <p className="text-muted-foreground">
                We retain your personal information for as long as your account is active or as needed to provide you services. We may also retain information to comply with legal obligations, resolve disputes, and enforce our agreements. When you delete your account, we will delete or anonymize your personal information within 90 days, unless retention is required by law.
              </p>
            </section>

            {/* Your Rights */}
            <section>
              <h2 className="text-2xl font-bold mb-4" style={{ fontFamily: 'Barlow Condensed' }}>7. YOUR RIGHTS</h2>
              <p className="text-muted-foreground mb-4">Under Australian privacy law, you have the right to:</p>
              <ul className="list-disc list-inside text-muted-foreground space-y-2">
                <li><strong>Access:</strong> Request a copy of the personal information we hold about you</li>
                <li><strong>Correction:</strong> Request correction of inaccurate or incomplete information</li>
                <li><strong>Deletion:</strong> Request deletion of your personal information (subject to legal requirements)</li>
                <li><strong>Opt-out:</strong> Unsubscribe from marketing communications at any time</li>
                <li><strong>Complaint:</strong> Lodge a complaint with the Office of the Australian Information Commissioner (OAIC)</li>
              </ul>
              <p className="text-muted-foreground mt-4">
                To exercise these rights, please contact us using the details provided below.
              </p>
            </section>

            {/* Cookies */}
            <section>
              <h2 className="text-2xl font-bold mb-4" style={{ fontFamily: 'Barlow Condensed' }}>8. COOKIES</h2>
              <p className="text-muted-foreground mb-4">
                We use cookies and similar tracking technologies to enhance your experience on our Platform. Cookies help us:
              </p>
              <ul className="list-disc list-inside text-muted-foreground space-y-2 mb-4">
                <li>Remember your login status and preferences</li>
                <li>Understand how you use our Platform</li>
                <li>Improve our services based on usage patterns</li>
              </ul>
              <p className="text-muted-foreground">
                You can control cookies through your browser settings. However, disabling cookies may affect your ability to use certain features of our Platform.
              </p>
            </section>

            {/* Third-Party Links */}
            <section>
              <h2 className="text-2xl font-bold mb-4" style={{ fontFamily: 'Barlow Condensed' }}>9. THIRD-PARTY LINKS</h2>
              <p className="text-muted-foreground">
                Our Platform may contain links to third-party websites. We are not responsible for the privacy practices or content of these websites. We encourage you to read the privacy policies of any third-party sites you visit.
              </p>
            </section>

            {/* Children's Privacy */}
            <section>
              <h2 className="text-2xl font-bold mb-4" style={{ fontFamily: 'Barlow Condensed' }}>10. CHILDREN'S PRIVACY</h2>
              <p className="text-muted-foreground">
                Our Platform is not intended for children under 18 years of age. We do not knowingly collect personal information from children. If you believe we have collected information from a child, please contact us immediately.
              </p>
            </section>

            {/* Changes to Policy */}
            <section>
              <h2 className="text-2xl font-bold mb-4" style={{ fontFamily: 'Barlow Condensed' }}>11. CHANGES TO THIS POLICY</h2>
              <p className="text-muted-foreground">
                We may update this Privacy Policy from time to time. We will notify you of any material changes by posting the new policy on our Platform and updating the "Last updated" date. Your continued use of the Platform after changes constitutes acceptance of the revised policy.
              </p>
            </section>

            {/* Contact */}
            <section>
              <h2 className="text-2xl font-bold mb-4" style={{ fontFamily: 'Barlow Condensed' }}>12. CONTACT US</h2>
              <p className="text-muted-foreground mb-4">
                If you have any questions about this Privacy Policy or wish to exercise your privacy rights, please contact us:
              </p>
              <div className="bg-muted/30 rounded-sm p-4">
                <p className="text-foreground font-medium">The Hotshot Marketplace</p>
                <p className="text-muted-foreground">Email: Admin@thehotshotmarketplace.com.au</p>
                <p className="text-muted-foreground">Website: www.thehotshotmarketplace.com.au</p>
              </div>
              <p className="text-muted-foreground mt-4">
                You may also contact the Office of the Australian Information Commissioner (OAIC) at <a href="https://www.oaic.gov.au" className="text-primary hover:underline" target="_blank" rel="noopener noreferrer">www.oaic.gov.au</a> if you have concerns about how we handle your personal information.
              </p>
            </section>

          </div>

          {/* Footer Note */}
          <div className="mt-12 pt-8 border-t border-border">
            <p className="text-sm text-muted-foreground text-center">
              This Privacy Policy is effective as of February 2026 and applies to all users of The Hotshot Marketplace.
            </p>
          </div>

        </motion.div>
      </main>
    </div>
  );
}
